/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

// TODO: Replace 'sh-services' dependency from Web core with one from a workspace package.
import { UserProfile } from "@microsoft/shifts-models-data";
import { InstrumentationService } from "sh-services";
import { AppDb } from "sh-services/data/AppDb";

import { IUserProfileDatabase } from "./IUserProfileDatabase";

/**
 * TODO: After fixing circular dependency, move 'UserProfileDatabase' to managers package under 'userProfile' folder.
 * The user profile database.
 */
export class UserProfileDatabase implements IUserProfileDatabase {
    /**
     * Instantiates a new instance of the UserProfileDatabase.
     * @param database The database.
     */
    constructor(private readonly database: AppDb) {}

    /**
     * Gets the users profiles from given users ids.
     * @param usersIds The users ids.
     * @returns The users profiles or undefined if no data was found. It might return partial data.
     */
    public async getUsersProfiles(usersIds: string[]): Promise<UserProfile[] | undefined> {
        let users: UserProfile[] | undefined = undefined;

        try {
            const records = await this.database.usersProfiles.where('userId').anyOf(usersIds).toArray();

            if (records.length > 0) {
                users = records.map(({ user }) => user);
            }
        } catch (error) {
            InstrumentationService.trackException(error, "UserProfileDatabase.getUsersProfiles");
        }

        return users;
    }

    /**
     * Sets the users profiles.
     * @param users The users profiles.
     */
    public async setUsersProfiles(users: UserProfile[]): Promise<void> {
        try {
            const records = users.map(user => ({ user, userId: user.id }));
            const usersIds = users.map(({ id }) => id);

            await this.database.usersProfiles.where({ userId: usersIds }).delete();
            await this.database.usersProfiles.bulkPut(records);
        } catch (error) {
            InstrumentationService.trackException(error, "UserProfileDatabase.setUsersProfiles");
        }
    }
}
